import React, { useEffect, useState, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom";
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { convertDecimalMinutesToDuration, scrollToView, showToaster } from "components/Common/common";
import { setBreadcrumbItems } from "../../store/actions"
import { connect } from "react-redux"
import { PAGECODE, PAGEPATHS, SIDEBARDATA, USERTYPES } from "components/Common/Constant";
import { useSelector } from "react-redux";
import { ModernButton, ModernCard, ModernBadge, ModernStatCard } from '../../components/ModernUI';
import '../Newdashboard/ComplianceReport.css'; // Import modal styles
import {
  Plus,
  RefreshCw,
  Search,
  Activity,
  Clock,
  CheckCircle,
  Globe,
  Calendar,
  Eye,
  BarChart2,
  FileText,
  FolderDown,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Trash2,
  Brain,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

// Sortable Table Header Component
const SortableHeader = ({ label, field, sortConfig, onSort, width }) => {
  const isActive = sortConfig.field === field;
  const Icon = isActive
    ? (sortConfig.direction === 'asc' ? ArrowUp : ArrowDown)
    : ArrowUpDown;

  return (
    <th
      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
      onClick={() => onSort(field)}
      style={width ? { width } : {}}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <Icon className={`w-3 h-3 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
      </div>
    </th>
  );
};

// Inline Action Buttons Component
const ActionButtons = ({ scan, onAction, hasWrite }) => {
  const allActions = [
    { id: 0, icon: Brain, tooltip: 'AI Dashboard', color: 'purple', requiresWrite: false },
    { id: 2, icon: BarChart2, tooltip: 'Dashboard', color: 'green', requiresWrite: false },
    // { id: 5, icon: Download, tooltip: 'Generate Reports', color: 'indigo', requiresWrite: true },
    { id: 7, icon: FolderDown, tooltip: 'Download Results', color: 'blue' },
    { id: 6, icon: Trash2, tooltip: 'Delete Scan', color: 'red', requiresWrite: true }
  ];

  // Hide write actions for read-only users
  const actions = allActions.filter(a => !a.requiresWrite || hasWrite);

  const colorClasses = {
    blue: 'hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200',
    green: 'hover:bg-green-50 hover:text-green-600 hover:border-green-200',
    purple: 'hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200',
    indigo: 'hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200',
    red: 'hover:bg-red-50 hover:text-red-600 hover:border-red-200'
  };

  return (
    <div className="flex items-center justify-center gap-1">
      {actions.map(action => {
        const Icon = action.icon;
        return (
          <button
            key={action.id}
            onClick={() => onAction(action.id, scan)}
            className={`p-1.5 rounded-md text-gray-600 border border-transparent transition-all duration-200 hover:shadow-sm ${colorClasses[action.color]}`}
            title={action.tooltip}
          >
            <Icon className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
};

// Live Timer Component for Running Scans
const LiveTimer = ({ scanStartTime, scanStartTimeRaw }) => {
  const [elapsedTime, setElapsedTime] = useState('0m 0s');

  useEffect(() => {
    console.log('LiveTimer props:', { scanStartTime, scanStartTimeRaw });

    if (!scanStartTime && !scanStartTimeRaw) {
      console.warn('LiveTimer: No start time provided');
      return;
    }

    const calculateElapsed = () => {
      try {
        let startTime;

        // Prefer raw timestamp if available (more reliable)
        if (scanStartTimeRaw) {
          startTime = new Date(scanStartTimeRaw);
          console.log('Using raw timestamp:', scanStartTimeRaw, '→', startTime);
        } else {
          // Parse backend formatted string: "Nov-12-2025 14:30:45"
          startTime = new Date(scanStartTime);
          console.log('Using formatted timestamp:', scanStartTime, '→', startTime);

          // If invalid, try parsing the custom format manually
          if (isNaN(startTime.getTime())) {
            const parts = scanStartTime.split(' ');
            if (parts.length === 2) {
              const datePart = parts[0]; // "Nov-12-2025"
              const timePart = parts[1]; // "14:30:45"

              // Convert to ISO format that JavaScript can parse
              const isoString = `${datePart.replace(/-/g, ' ')} ${timePart}`;
              startTime = new Date(isoString);
              console.log('Parsed custom format:', isoString, '→', startTime);
            }
          }
        }

        if (isNaN(startTime.getTime())) {
          console.warn('Could not parse scan start time:', scanStartTime, scanStartTimeRaw);
          return '0m 0s';
        }

        const now = new Date();
        const diffInSeconds = Math.floor((now - startTime) / 1000);

        // Handle negative time (clock skew)
        if (diffInSeconds < 0) {
          console.warn('Negative time difference:', diffInSeconds);
          return '0m 0s';
        }

        const minutes = Math.floor(diffInSeconds / 60);
        const seconds = diffInSeconds % 60;

        return `${minutes}m ${seconds}s`;
      } catch (error) {
        console.error('Error calculating elapsed time:', error);
        return '0m 0s';
      }
    };

    // Initial calculation
    setElapsedTime(calculateElapsed());

    // Update every second
    const interval = setInterval(() => {
      setElapsedTime(calculateElapsed());
    }, 1000);

    return () => clearInterval(interval);
  }, [scanStartTime, scanStartTimeRaw]);

  return (
    <div className="flex items-center space-x-1">
      <span className="text-xs text-blue-600 font-medium">{elapsedTime}</span>
      <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></span>
    </div>
  );
};

// Scanner display names for better readability
const SCANNER_LABELS = {
  'pshtt': 'HTTPS Check',
  'a11y': 'Accessibility',
  '200scanner': 'HTTP Status',
  'analytics': 'Analytics',
  'csp': 'CSP Headers',
  'dap': 'DAP Analytics',
  'noop': 'No-Op',
  'pagedata': 'Page Data',
  'privacy': 'Privacy',
  'seo': 'SEO',
  'sitemap': 'Sitemap',
  'sslyze': 'SSL/TLS',
  'third_parties': 'Third Parties',
  'trustymail': 'Email Security',
  'uswds': 'USWDS v1',
  'uswds2': 'USWDS v2',
  'wapiti_scan': 'Wapiti',
  'zaproxy_scan': 'ZAP Proxy',
  'lighthouse_scan': 'Lighthouse',
  'lighthouse_desktop_scan': 'Lighthouse Desktop',
  'outbound_link_scan': 'Outbound Links',
  'wappybird_scan': 'Tech Stack',
  'search_feature_scan': 'Search Feature',
  'gov_analytics': 'Gov Analytics',
  'nikto_scan': 'Nikto'
};

const formatScannerDuration = (seconds) => {
  if (seconds < 1) return '<1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
};

// Scan Progress Panel - shown when a row is expanded
const ScanProgressPanel = ({ scanId, isRunning }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let interval = null;

    const fetchProgress = async () => {
      try {
        const res = await axiosInstance.get(ApiNames.SCAN_PROGRESS(scanId));
        if (mounted && res.data?.status === 1) {
          setProgress(res.data);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) setLoading(false);
      }
    };

    fetchProgress();

    // Poll every 5 seconds for running scans
    if (isRunning) {
      interval = setInterval(fetchProgress, 5000);
    }

    return () => {
      mounted = false;
      if (interval) clearInterval(interval);
    };
  }, [scanId, isRunning]);

  if (loading) {
    return (
      <div className="px-6 py-4 text-center text-sm text-gray-500">
        <RefreshCw className="w-4 h-4 inline-block animate-spin mr-2" />
        Loading scanner progress...
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="px-6 py-3 text-sm text-gray-500">
        No progress data available yet.
      </div>
    );
  }

  const { completedScanners, totalScanners, scanners, phase } = progress;
  const pct = totalScanners > 0 ? Math.round((completedScanners / totalScanners) * 100) : 0;

  // Sort: done scanners first (by end_time), then pending
  const sorted = [...(scanners || [])].sort((a, b) => {
    if (a.status === 'done' && b.status !== 'done') return -1;
    if (a.status !== 'done' && b.status === 'done') return 1;
    if (a.status === 'done' && b.status === 'done') {
      return new Date(a.end_time) - new Date(b.end_time);
    }
    return 0;
  });

  return (
    <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-blue-50">
      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${phase === 'completed' ? 'bg-green-500' : 'bg-blue-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
          {completedScanners}/{totalScanners} scanners
        </span>
        {phase === 'initializing' && (
          <span className="text-xs text-amber-600 font-medium">Initializing...</span>
        )}
      </div>

      {/* Scanner grid */}
      <div className="grid grid-cols-5 gap-1.5">
        {sorted.map((scanner) => {
          const isDone = scanner.status === 'done';
          const label = SCANNER_LABELS[scanner.name] || scanner.name;
          return (
            <div
              key={scanner.name}
              className={`flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs transition-all ${isDone
                ? 'bg-white border border-green-200 text-gray-700'
                : 'bg-white/60 border border-gray-200 text-gray-400'
                }`}
              title={isDone ? `${label}: ${formatScannerDuration(scanner.duration)}` : `${label}: pending`}
            >
              <span className="truncate font-medium">{label}</span>
              {isDone ? (
                <span className="ml-1.5 text-green-600 font-semibold whitespace-nowrap">{formatScannerDuration(scanner.duration)}</span>
              ) : (
                <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Total duration for completed scans */}
      {progress.totalDuration && (
        <div className="mt-2.5 text-xs text-gray-500 text-right">
          Total scan time: <span className="font-semibold text-gray-700">{formatScannerDuration(progress.totalDuration)}</span>
        </div>
      )}
    </div>
  );
};

// Helper function to strip http://, https://, and trailing slashes from URLs for display
const stripProtocol = (url) => {
  if (!url) return url;
  return url
    .trim()                        // Remove whitespace first
    .replace(/^https?:\/\//, '')   // Remove protocol only
    .replace(/\/+$/, '');          // Remove trailing slashes
};

const ScanList = (props) => {
  document.title = "Scan List | GOVCOM";
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, scanId: null, scanName: null });
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [expandedScanId, setExpandedScanId] = useState(null);

  useEffect(() => {
    props.setBreadcrumbItems("Scan List", [])
  })

  const user = useSelector((state) => state?.users.users)
  const hasWritePermission16 = user?.permissions?.write?.includes(PAGECODE["Scan List"]);

  const handleClick = async (type, data) => {
    sessionStorage.setItem('Url', data.scannedUrl || data.Domain)
    sessionStorage.setItem('domain', data.Domain)

    if (type === 0) {
      sessionStorage.setItem('scanId', data._id);
      navigate(`${PAGEPATHS.AIHub}/${data._id}`);
    }
    if (type === 1) {
      let userOrOrgId = user.userType === USERTYPES.Standard
        ? '&userId=' + user.userId
        : user.userType === USERTYPES.Organization
          ? '&orgId=' + user.orgId
          : ''
      await fetchData(`${ApiNames.LIST_SCANS}?scanId=${data._id}&url=${encodeURIComponent(data.Url)}${userOrOrgId}`)
      scrollToView("specific-scan-details")
    }
    if (type === 2) {
      sessionStorage.setItem('scanId', data._id)

      // Check if report exists for this scan
      try {
        const checkReportResponse = await axiosInstance.get(`${ApiNames.SCAN_REPORTS}`, { timeout: 30000 });
        const scanReports = checkReportResponse.data?.scan_reports || [];
        const reportExists = scanReports.some(report => report?.scan_id === data._id);

        if (!reportExists) {
          // Report doesn't exist, generate it first
          showToaster(1, "Generating compliance report for this scan...");

          const generateResponse = await axiosInstance.post(ApiNames.GENERATE_REPORT_FROM_SCAN(data._id), {}, { timeout: 120000 });

          if (generateResponse.data?.status === 'success') {
            showToaster(1, `Report generated successfully! Overall compliance: ${generateResponse.data.overall_compliance}%`);
            // Wait a moment for the report to be saved
            await new Promise(resolve => setTimeout(resolve, 500));
            navigate(`${PAGEPATHS.Dashboard}/${data._id}`);
          } else {
            showToaster(0, "Failed to generate report. Please try again.");
          }
        } else {
          // Report exists, navigate directly
          navigate(`${PAGEPATHS.Dashboard}/${data._id}`);
        }
      } catch (error) {
        console.error("Error checking/generating report:", error);
        showToaster(0, "Error: " + (error.response?.data?.error || error.message || "Failed to load dashboard"));
      }
    }
    if (type === 3) {
      sessionStorage.setItem('scanId', data._id)
      navigate(`${PAGEPATHS.ComplianceDashboard}/${data._id}`);
    }
    if (type === 4) {
      sessionStorage.setItem('scanId', data._id)
      navigate(`${PAGEPATHS.CustomerDashbord}/${data._id}`);
    }
    if (type === 5) {
      try {
        const res = await axiosInstance.post("/processor/run", {
          urlid: data.urlid,
          targetName: data.Domain
        }, { timeout: 60000 });
        if (res.data.status === 1) {
          console.log("Generated Report:", res.data.data);
          showToaster(1, "Report generated successfully!");
          const { targetId } = res.data.data;
          navigate(`${PAGEPATHS.ComprehensiveComplianceDashboard}/${targetId}`);
        } else {
          showToaster(0, res.data.message || "Failed to generate report");
        }
      } catch (err) {
        console.error(err);
        showToaster(0, "Error generating report");
      }
    }
    if (type === 6) {
      // Delete scan - show confirmation modal
      const urlDisplay = data.Domain || data.scannedUrl || 'Unknown';
      setDeleteConfirm({
        show: true,
        scanId: data._id,
        scanName: stripProtocol(urlDisplay)
      });
    }

     if (type === 7) {
      try {
        const downloadUrl = ApiNames.DOWNLOAD_SCAN_OUTPUT(data._id);

        // Use axios to fetch the file as a blob
        // This allows us to catch errors (like 404) before trying to save/open
        const response = await axiosInstance.get(downloadUrl, {
          responseType: 'blob'
        });

        // Create a URL for the blob
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // Set filename (try to get from content-disposition header if available, otherwise default)
        // const contentDisposition = response.headers['content-disposition'];
        // let fileName = `${data._id}_scan_results.zip`;
        // if (contentDisposition) {
        //   const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        //   if (fileNameMatch.length === 2)
        //     fileName = fileNameMatch[1];
        // }

        link.setAttribute('download', `${data._id}_scan_results.zip`);
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error("Download error:", error);
        // Check for specific error status (e.g. 404)
        if (error.response && error.response.status === 404) {
          showToaster(0, "Scan result not found");
        } else {
          showToaster(0, "Failed to download scan results.");
        }
      }
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.scanId) {
      console.error('No scanId provided for deletion');
      showToaster(0, "Error: No scan ID found");
      return;
    }

    console.log('Attempting to delete scan:', {
      scanId: deleteConfirm.scanId,
      scanName: deleteConfirm.scanName,
      url: `${ApiNames.DELETE_SCAN}/${deleteConfirm.scanId}`
    });

    try {
      setDeleting(true);
      const res = await axiosInstance.delete(`${ApiNames.DELETE_SCAN}/${deleteConfirm.scanId}`);

      console.log('Delete response:', res);

      if (res.data.status === 1 || res.status === 200) {
        showToaster(1, "Scan deleted successfully!");
        await getScanList();
        setDeleteConfirm({ show: false, scanId: null, scanName: null });
      } else {
        console.error('Delete failed with response:', res.data);
        showToaster(0, res.data.message || "Failed to delete scan");
      }
    } catch (err) {
      console.error("Error deleting scan:", err);
      console.error("Error response:", err.response);
      console.error("Error message:", err.message);
      showToaster(0, err.response?.data?.message || err.message || "Error deleting scan");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, scanId: null, scanName: null });
  };

  const getRedirectionRoute = (scan) => {
    const routeMap = {
      outbound: PAGEPATHS.Siteurlsummary,
      pagedata: PAGEPATHS.Siteurlsummary,
      third: PAGEPATHS.Siteurlsummary,
      a11y: PAGEPATHS.Accessibility,
      lighthouse: PAGEPATHS.Accessibility,
      analytics: PAGEPATHS.Analytics,
      dap: PAGEPATHS.Analytics,
      gov: PAGEPATHS.Analytics,
      pshtt: PAGEPATHS.Securitycomp,
      sslyze: PAGEPATHS.Securitycomp,
      csp: PAGEPATHS.Securitycomp,
      seo: PAGEPATHS.Searchengine,
      search: PAGEPATHS.Searchengine,
      trustymail: PAGEPATHS.Mailserver,
      uswds2: PAGEPATHS.Uswebdesignstd,
      wappybird: PAGEPATHS.Technologystack,
      zaproxy: PAGEPATHS.Vulnerability
    };
    return routeMap[scan] || null;
  }

  const redirectScanDetailsPage = (id, name) => {
    const route = getRedirectionRoute(name)

    if (!route) {
      showToaster(0, "Scan not available")
      return
    }

    const pageKey = SIDEBARDATA.find(obj => obj.route === route)

    if (!user?.permissions?.read || !user.permissions.read.includes(pageKey?.pageCode)) {
      showToaster(0, "Insufficient Permission: Contact admin.")
      return
    }

    const oldScanId = sessionStorage.getItem('scanId')
    const existing = sessionStorage.getItem('pages')
    sessionStorage.setItem('scanId', id)

    if (pageKey && pageKey.pageCode) {
      if (oldScanId !== id) {
        sessionStorage.setItem('pages', pageKey.pageCode)
      } else if (existing && existing.split(';').includes(pageKey.pageCode.toString())) {
        console.log('No changes made in routes');
      } else if (existing) {
        sessionStorage.setItem('pages', `${existing};${pageKey.pageCode}`)
      } else {
        sessionStorage.setItem('pages', pageKey.pageCode)
      }
    }
    navigate(route)
  }

  const [processedScans, setProcessedScans] = useState([]);
  const [scanDetailsRows, setScanDetailsRows] = useState([]);

  const fetchData = async (url) => {
    const { data } = await axiosInstance.get(url)
    const validData = data.data.reduce((a, v) => {
      const { totalScanDuration, ...other } = v
      const formattedTsd = convertDecimalMinutesToDuration(totalScanDuration)
      const format = {
        ...other,
        totalScanDuration: formattedTsd,
      }

      // Debug logging for running scans
      if (v.status === 'running') {
        console.log('Running scan data:', {
          Domain: v.Domain,
          scanStartTime: v.scanStartTime,
          scanStartTimeRaw: v.scanStartTimeRaw,
          status: v.status
        });
      }

      a.push(format)
      return a
    }, [])

    if (url.includes('scanId')) {
      setScanDetailsRows(validData);
    } else {
      setProcessedScans(validData);
    }
  }

  // Memoized function to get scan list
  const getScanList = useCallback(async () => {
    if (!user) return;

    try {
      const concat = user.userType === USERTYPES.Standard
        ? `?userId=${user.userId}`
        : user.userType === USERTYPES.Organization
          ? `?orgId=${user.orgId}`
          : ''

      await fetchData(ApiNames.LIST_SCANS + concat)
    } catch (e) {
      console.error(e);
    }
  }, [user]);

  // Initial load
  useEffect(() => {
    if (user) {
      getScanList();
    }
  }, [user, getScanList]);

  // Auto-refresh every 10 seconds if there are running scans
  useEffect(() => {
    const hasRunningScans = processedScans.some(scan => scan.status === 'running');

    if (!hasRunningScans) return;

    const autoRefreshInterval = setInterval(() => {
      console.log('Auto-refreshing scan list...');
      getScanList();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(autoRefreshInterval);
  }, [processedScans, getScanList])

  // Calculate stats
  const stats = useMemo(() => {
    const total = processedScans.length;
    const completed = processedScans.filter(s => s.status === 'completed' || s.scanStatus === 'SUCCESS').length;
    const running = processedScans.filter(s => s.status === 'running').length;
    const failed = processedScans.filter(s => s.scanStatus === 'FAILED').length;

    // Calculate average time only for completed scans with valid durations
    const completedScans = processedScans.filter(s =>
      (s.status === 'completed' || s.scanStatus === 'SUCCESS') &&
      s.totalScanDuration &&
      s.totalScanDuration !== 'N/A'
    );

    const avgTime = completedScans.length > 0
      ? Math.round(completedScans.reduce((sum, s) => {
        const durationStr = s.totalScanDuration;
        // Extract minutes from format "Xm Ys"
        const minutesMatch = durationStr.match(/(\d+)m/);
        const secondsMatch = durationStr.match(/(\d+)s/);
        const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
        const seconds = secondsMatch ? parseInt(secondsMatch[1]) : 0;
        return sum + minutes + (seconds / 60);
      }, 0) / completedScans.length * 10) / 10
      : 0;

    return { total, completed, running, failed, avgTime };
  }, [processedScans]);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter and sort scans
  const filteredScans = useMemo(() => {
    let filtered = searchTerm
      ? processedScans.filter(scan =>
        scan.Url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scan.Domain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scan._id?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      : [...processedScans];

    // Apply sorting if configured
    if (sortConfig.field) {
      filtered.sort((a, b) => {
        let aVal, bVal;

        switch (sortConfig.field) {
          case 'url':
            aVal = (a.Domain || a.Url || '').toLowerCase();
            bVal = (b.Domain || b.Url || '').toLowerCase();
            break;
          case 'status':
            aVal = a.status || '';
            bVal = b.status || '';
            break;
          case 'user':
            aVal = (a.username || '').toLowerCase();
            bVal = (b.username || '').toLowerCase();
            break;
          case 'organization':
            aVal = (a.oragnizationName || '').toLowerCase();
            bVal = (b.oragnizationName || '').toLowerCase();
            break;
          case 'startTime':
            aVal = a.scanStartTimeRaw ? new Date(a.scanStartTimeRaw).getTime() : 0;
            bVal = b.scanStartTimeRaw ? new Date(b.scanStartTimeRaw).getTime() : 0;
            break;
          case 'endTime':
            aVal = a.scanEndTime === 'In Progress' ? Date.now() : new Date(a.scanEndTime).getTime();
            bVal = b.scanEndTime === 'In Progress' ? Date.now() : new Date(b.scanEndTime).getTime();
            break;
          case 'duration':
            aVal = a.totalScanDuration === 'In Progress' ? 99999999 : parseFloat(a.totalScanDuration) || 0;
            bVal = b.totalScanDuration === 'In Progress' ? 99999999 : parseFloat(b.totalScanDuration) || 0;
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [processedScans, searchTerm, sortConfig]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredScans.length / itemsPerPage);
  const paginatedScans = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredScans.slice(startIndex, endIndex);
  }, [filteredScans, currentPage, itemsPerPage]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const concat = user.userType === USERTYPES.Standard
        ? `?userId=${user.userId}`
        : user.userType === USERTYPES.Organization
          ? `?orgId=${user.orgId}`
          : ''
      await fetchData(ApiNames.LIST_SCANS + concat)
      showToaster(1, "Scan list refreshed successfully");
    } catch (e) {
      console.error(e);
      showToaster(0, "Failed to refresh scan list");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Don't render until user data is loaded
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50" style={{ margin: '-2.5rem', marginTop: '-6rem', padding: '2rem', paddingTop: '4rem' }}>
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Scan List</h1>
              <p className="text-gray-600 mt-1">Manage and view all compliance scans</p>
            </div>
            <div className="flex items-center space-x-3">
              <ModernButton
                variant="secondary"
                icon={RefreshCw}
                size="md"
                onClick={handleRefresh}
                loading={isRefreshing}
              >
                Refresh
              </ModernButton>
              {hasWritePermission16 && (
                <ModernButton
                  variant="primary"
                  icon={Plus}
                  size="md"
                  onClick={() => navigate(PAGEPATHS.StartAScan)}
                >
                  New Scan
                </ModernButton>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <ModernStatCard
            title="Total Scans"
            value={stats.total.toLocaleString()}
            icon={Activity}
            iconBgColor="bg-blue-500"
          />
          <ModernStatCard
            title="Completed"
            value={stats.completed.toLocaleString()}
            icon={CheckCircle}
            iconBgColor="bg-green-500"
          />
          <ModernStatCard
            title="Failed"
            value={stats.failed.toLocaleString()}
            icon={Globe}
            iconBgColor="bg-red-500"
          />
          <ModernStatCard
            title="Avg Duration"
            value={`${stats.avgTime}m`}
            icon={Clock}
            iconBgColor="bg-purple-500"
            description="per scan"
          />
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by URL, domain, or scan ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Main Scans Table */}
        <ModernCard
          title="All Scans"
          subtitle={`${filteredScans.length} ${filteredScans.length === 1 ? 'scan' : 'scans'} found`}
          icon={FileText}
        >
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader label="URL/Domain" field="url" sortConfig={sortConfig} onSort={handleSort} width="20%" />
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                    Scan ID
                  </th>
                  <SortableHeader label="Status" field="status" sortConfig={sortConfig} onSort={handleSort} width="8%" />
                  {user?.userType !== USERTYPES.Standard && (
                    <>
                      <SortableHeader label="User" field="user" sortConfig={sortConfig} onSort={handleSort} width="10%" />
                      {user?.userType === USERTYPES.Global && (
                        <SortableHeader label="Org" field="organization" sortConfig={sortConfig} onSort={handleSort} width="10%" />
                      )}
                    </>
                  )}
                  <SortableHeader label="Started" field="startTime" sortConfig={sortConfig} onSort={handleSort} width="15%" />
                  <SortableHeader label="Time" field="duration" sortConfig={sortConfig} onSort={handleSort} width="8%" />
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '12%' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredScans.length === 0 ? (
                  <tr>
                    <td colSpan={user?.userType === USERTYPES.Global ? 8 : user?.userType === USERTYPES.Standard ? 6 : 7} className="px-6 py-8 text-center text-gray-500">
                      No scans found
                    </td>
                  </tr>
                ) : (
                  paginatedScans.map((scan, idx) => {
                    const isExpanded = expandedScanId === scan._id;
                    const colCount = user?.userType === USERTYPES.Global ? 8 : user?.userType === USERTYPES.Standard ? 6 : 7;
                    return (
                      <React.Fragment key={idx}>
                        <tr
                          className={`hover:bg-gray-50 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50/40' : ''}`}
                          onClick={() => setExpandedScanId(isExpanded ? null : scan._id)}
                        >
                          <td className="px-3 py-3 text-sm max-w-xs">
                            <div className="flex items-center space-x-2">
                              {isExpanded
                                ? <ChevronDown className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              }
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-gray-900 truncate" title={scan.Domain}>
                                  {scan.Domain || 'Domain'}
                                </div>
                                {scan.scannedUrl && stripProtocol(scan.scannedUrl) !== stripProtocol(scan.Domain) && (
                                  <span
                                    className="text-xs text-blue-600 truncate block"
                                    title={scan.scannedUrl}
                                  >
                                    {stripProtocol(scan.scannedUrl)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-600 font-mono break-all">
                            {scan._id}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <ModernBadge
                              variant={
                                scan.status === 'completed' || scan.scanStatus === 'SUCCESS'
                                  ? 'success'
                                  : scan.status === 'running'
                                    ? 'warning'
                                    : scan.scanStatus === 'FAILED'
                                      ? 'danger'
                                      : 'info'
                              }
                            >
                              {scan.status === 'running'
                                ? 'Running'
                                : scan.status === 'completed'
                                  ? 'Done'
                                  : scan.scanStatus === 'FAILED'
                                    ? 'Failed'
                                    : 'Unknown'}
                            </ModernBadge>
                          </td>
                          {user?.userType !== USERTYPES.Standard && (
                            <>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 max-w-[120px] truncate" title={scan.username}>
                                {scan.username}
                              </td>
                              {user?.userType === USERTYPES.Global && (
                                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 max-w-[120px] truncate" title={scan.organizationName}>
                                  {scan.organizationName}
                                </td>
                              )}
                            </>
                          )}
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-3 h-3 text-gray-400" />
                              <span className="text-xs">{scan.scanStartTime}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3 text-gray-400" />
                              {scan.status === 'running' ? (
                                <LiveTimer
                                  scanStartTime={scan.scanStartTime}
                                  scanStartTimeRaw={scan.scanStartTimeRaw}
                                />
                              ) : (
                                <span className="text-xs">{scan.totalScanDuration}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <ActionButtons scan={scan} onAction={handleClick} hasWrite={hasWritePermission16} />
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={colCount} className="p-0 border-b border-blue-100">
                              <ScanProgressPanel scanId={scan._id} isRunning={scan.status === 'running'} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {filteredScans.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, filteredScans.length)}
                </span>{' '}
                of <span className="font-medium">{filteredScans.length}</span> scans
              </div>

              <div className="flex items-center space-x-2">
                <ModernButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </ModernButton>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current
                    const showPage =
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1);

                    const showEllipsis =
                      (page === 2 && currentPage > 3) ||
                      (page === totalPages - 1 && currentPage < totalPages - 2);

                    if (showEllipsis) {
                      return (
                        <span key={page} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }

                    if (!showPage) return null;

                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${currentPage === page
                          ? 'bg-blue-600 text-white font-medium'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <ModernButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </ModernButton>
              </div>
            </div>
          )}
        </ModernCard>

        {/* Scan Details Section */}
        {scanDetailsRows.length > 0 && (
          <div id="specific-scan-details" className="mt-6">
            <ModernCard
              title="Scan Details"
              subtitle={sessionStorage.getItem('Url') || ''}
              icon={FileText}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        URL
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Version
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Scan Name
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Started
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ended
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {scanDetailsRows.map((detail, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-3 text-sm text-gray-900 max-w-xs truncate" title={detail.URL}>
                          {stripProtocol(detail.URL)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600">
                          {detail.VersionId}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                          {detail.ScanName}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                          {detail.scanStartTime}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                          {detail.scanEndTime}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                          {detail.totalScanDuration}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-center text-sm">
                          <ModernButton
                            variant="link"
                            size="sm"
                            onClick={() => redirectScanDetailsPage(detail.ScanID, detail.ScanName)}
                          >
                            View
                          </ModernButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ModernCard>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-icon-wrapper">
              <Trash2 className="modal-icon" />
            </div>

            <h3 className="modal-title">Delete Scan</h3>

            <p className="modal-description">
              Are you sure you want to delete the scan for{' '}
              <span className="font-medium text-gray-900">{deleteConfirm.scanName}</span>
              ? This action cannot be undone and will remove all associated scan data.
            </p>

            <div className="modal-actions">
              <ModernButton
                variant="secondary"
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="flex-1"
              >
                Cancel
              </ModernButton>
              <ModernButton
                variant="danger"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                loading={deleting}
                className="flex-1"
              >
                Delete
              </ModernButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default connect(null, { setBreadcrumbItems })(ScanList)